import * as React from "react";

import { TextInput, type TextInputSize } from "@/shared/foundation/primitives";

import { isValueless, type NumericComparator } from "../FilterSchema";

import styles from "./valueInputs.module.css";

interface NumericValueInputProps {
  comparator: NumericComparator;
  value: number[];
  onChange: (next: number[]) => void;
  /** Optional unit of measure (e.g. "%", "ms"), shown inside the input's end
   *  so the entered value reads with its unit. Omitted/`null` shows none. */
  unit?: string | null;
  /**
   * Take focus on mount. The filter menu wants it; an editor that renders
   * several inputs at once does not. @default true
   */
  autoFocus?: boolean;
  /** Marks the input invalid, for an error shown beside it. */
  invalid?: boolean;
  /** Field size, on the `TextInput` scale. @default "md" */
  size?: TextInputSize;
}

/**
 * A single-number value input for numeric and metric filters.
 *
 * The field holds the text the user is typing, not the number it parses to.
 * Each keystroke reports the parsed number through `onChange` (or `[]` while
 * the text is empty or not yet a number), so the parent's validation stays
 * live, but the text itself is left alone until the field loses focus. A
 * field that re-rendered the parsed number as its text would rewrite "05" to
 * "5" and "5.0" to "5" mid-edit, which made a leading zero impossible to type
 * and dropped the decimal point the user was about to continue.
 */
export function NumericValueInput({
  comparator,
  value,
  onChange,
  unit = null,
  autoFocus = true,
  invalid = false,
  size = "md",
}: NumericValueInputProps) {
  const valueless = isValueless(comparator);
  const committed = value.length === 1 ? value[0] : null;

  const [draft, setDraft] = React.useState(() => formatNumber(committed));
  // The committed value as of the previous render, to tell a change made by
  // the parent (an operator switch clearing the value, a new filter seeded
  // in) from the echo of this field's own `onChange`. Only the former
  // replaces the draft, and only when the draft does not already read as the
  // new value.
  const [seenCommitted, setSeenCommitted] = React.useState(committed);
  if (!Object.is(committed, seenCommitted)) {
    setSeenCommitted(committed);
    if (!Object.is(parseNumber(draft), committed)) {
      setDraft(formatNumber(committed));
    }
  }

  if (valueless) {
    return null;
  }

  const input = (
    <TextInput
      type="number"
      autoFocus={autoFocus}
      aria-invalid={invalid || undefined}
      size={size}
      placeholder="Enter number..."
      value={draft}
      className={unit ? styles.inputWithUnit : undefined}
      onChange={(event) => {
        const raw = event.target.value;
        setDraft(raw);
        const parsed = parseNumber(raw);
        onChange(parsed === null ? [] : [parsed]);
      }}
      onBlur={() => {
        // "05" and "5.0" were only ever ways of typing 5; once the user is
        // done, show the number the filter will carry.
        setDraft(formatNumber(committed));
      }}
    />
  );
  if (!unit) {
    return input;
  }
  return (
    <div className={styles.withUnit}>
      {input}
      <span className={styles.unit} aria-hidden="true">
        {unit}
      </span>
    </div>
  );
}

/** The number a field's text stands for, or `null` when it is empty or not a number. */
function parseNumber(text: string): number | null {
  if (text === "") {
    return null;
  }
  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : null;
}

function formatNumber(value: number | null): string {
  return value === null ? "" : value.toString();
}
